import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaFileAlt, FaCheckCircle, FaTimesCircle, FaExclamationCircle } from 'react-icons/fa';
import { Page, PageHeader, Card, Badge, Field, Alert, Loading, formatDate, initials } from '../../ui';
import { verificationTone, docLabel } from '../../ui/staff';
import { api } from '../../ui/api';
import { documentLink } from '../../session';

const DECISIONS = [
  { value: 'Verified', icon: FaCheckCircle, hint: 'Documents check out. They can sign in and take jobs.' },
  { value: 'Action Required', icon: FaExclamationCircle, hint: 'Something needs fixing. They stay blocked until re-checked.' },
  { value: 'Rejected', icon: FaTimesCircle, hint: 'Not approved. They cannot sign in.' },
];

const StaffDetails = () => {
  const { staffId } = useParams();
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState('');
  const [decision, setDecision] = useState('');
  const [remarks, setRemarks] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api(`/staff/get_staff/${staffId}`).then(setStaff).catch((e) => setError(e.message));
  }, [staffId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!decision) return setFormError('Choose a decision.');
    if (decision !== 'Verified' && !remarks.trim()) return setFormError('Add a note explaining what is wrong.');
    setSaving(true);
    setFormError('');
    try {
      setStaff(await api(`/staff/update_verification/${staffId}`, { method: 'PUT', body: { verification: decision, remarks } }));
      setDecision('');
      setRemarks('');
      setSaved(true);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Page><Alert>{error}</Alert></Page>;
  if (!staff) return <Page><Loading /></Page>;

  const docs = Object.entries(staff.docs_list || {}).filter(([, url]) => url);

  return (
    <Page>
      <PageHeader
        title={staff.name}
        subtitle={staff.vendor_id ? `Field staff · ${staff.vendor_id.company_name}` : 'Field staff'}
        back={{ to: '/manage-staff', label: 'Field staff' }}
        actions={<Badge tone={verificationTone(staff.verification)}>{staff.verification || 'Pending'}</Badge>}
      />
      {saved && <Alert tone="success">Verification updated.</Alert>}
      <div className="grid grid-sidebar">
        <div className="stack">
          <Card title="Profile">
            <div className="row" style={{ flexWrap: 'nowrap', gap: 14, marginBottom: 20 }}>
              <div className="avatar lg">{initials(staff.name)}</div>
              <div>
                <div className="strong">{staff.name}</div>
                <div className="small muted">Added {formatDate(staff.date_created)}</div>
              </div>
            </div>
            <dl className="kv">
              <div><dt>Phone</dt><dd>{staff.phone_number}</dd></div>
              <div><dt>Email</dt><dd>{staff.email || '—'}</dd></div>
              <div><dt>Aadhaar number</dt><dd className="mono">{staff.aadhar_number || '—'}</dd></div>
              <div><dt>Can sign in</dt><dd>{staff.is_active ? 'Yes' : 'No'}</dd></div>
              {staff.vendor_id && <div><dt>Vendor</dt><dd>{staff.vendor_id.company_name}</dd></div>}
              {staff.vendor_id && <div><dt>Vendor contact</dt><dd>{staff.vendor_id.representative_name} · {staff.vendor_id.company_phone}</dd></div>}
            </dl>
          </Card>
          <Card title="Documents" hint="Open each one before deciding.">
            {docs.length ? (
              <div className="grid grid-2" style={{ gap: 10 }}>
                {docs.map(([key, url]) => (
                  <a key={key} className="doc-tile" href={documentLink(url)} target="_blank" rel="noopener noreferrer">
                    <FaFileAlt /><span className="doc-name">{docLabel(key)}</span><span className="small muted">Open</span>
                  </a>
                ))}
              </div>
            ) : <p className="muted">No documents uploaded.</p>}
          </Card>
        </div>

        <div className="stack sticky-side">
          {staff.remarks && (
            <Card title="Latest note">
              <p>{staff.remarks}</p>
            </Card>
          )}
          <Card title="Verification decision">
            <form className="form" onSubmit={submit}>
              <div className="vendor-pick">
                {DECISIONS.map(({ value, icon: Icon, hint }) => (
                  <button type="button" key={value} className={`vendor-option${decision === value ? ' on' : ''}`} onClick={() => { setDecision(value); setFormError(''); }}>
                    <Icon style={{ color: `var(--${value === 'Verified' ? 'brand' : value === 'Rejected' ? 'danger' : 'warning'})`, flexShrink: 0 }} />
                    <div className="grow">
                      <div className="strong">{value}</div>
                      <div className="small muted">{hint}</div>
                    </div>
                  </button>
                ))}
              </div>
              {decision && decision !== 'Verified' && (
                <Field label="Note for the vendor">
                  <textarea className="textarea" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="What needs to change?" />
                </Field>
              )}
              <Alert>{formError}</Alert>
              <button className="btn btn-primary btn-block" disabled={!decision || saving}>{saving ? 'Saving…' : 'Save decision'}</button>
            </form>
          </Card>
        </div>
      </div>
    </Page>
  );
};

export default StaffDetails;
